import { useState, useEffect, useCallback } from 'react'
import { PageHeader, StatusBadge } from '../../components/ui'
import { api } from '../../services/api'

interface MigrationStatus {
  readonly mode: 'receiving' | 'locked' | 'live'
  readonly startedAt?: string
  readonly lastIngestAt: string | null
  readonly entityCounts: Record<string, number>
  readonly lastError?: { code: string; message: string; at: string }
}

function modeBadgeStatus(mode: string): 'active' | 'warning' | 'info' {
  if (mode === 'live') return 'active'
  if (mode === 'locked') return 'warning'
  return 'info'
}

function lagLabel(lastIngestAt: string | null): string {
  if (!lastIngestAt) return 'No data received'
  const ms = Date.now() - new Date(lastIngestAt).getTime()
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function MigrationStatusPage() {
  const [status, setStatus] = useState<MigrationStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<MigrationStatus>('/system-config/migration_status')
      setStatus(res.data ?? null)
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        setStatus(null)
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load migration status')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  if (loading) {
    return (
      <div>
        <PageHeader title="Migration Status" />
        <p className="text-gray-500">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Migration Status" />
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  if (!status) {
    return (
      <div>
        <PageHeader title="Migration Status" />
        <p className="text-gray-500">No active migration configured for this instance.</p>
      </div>
    )
  }

  const entityEntries = Object.entries(status.entityCounts)

  return (
    <div>
      <PageHeader title="Migration Status" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border rounded p-4">
          <div className="text-sm text-gray-500 mb-1">Mode</div>
          <StatusBadge status={modeBadgeStatus(status.mode)} label={status.mode} />
        </div>
        <div className="border rounded p-4">
          <div className="text-sm text-gray-500 mb-1">Last Ingest</div>
          <div className="font-medium">{lagLabel(status.lastIngestAt)}</div>
        </div>
        <div className="border rounded p-4">
          <div className="text-sm text-gray-500 mb-1">Entities Migrated</div>
          <div className="font-medium">{entityEntries.length} types</div>
        </div>
      </div>

      {entityEntries.length > 0 && (
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Entity</th>
                <th className="text-right p-3">Count</th>
              </tr>
            </thead>
            <tbody>
              {entityEntries.map(([entity, count]) => (
                <tr key={entity} className="border-b last:border-0">
                  <td className="p-3">{entity}</td>
                  <td className="p-3 text-right font-mono">{count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status.lastError && (
        <div className="mt-4 border border-red-200 rounded p-4 bg-red-50">
          <div className="text-sm font-medium text-red-800">Last Error</div>
          <div className="text-sm text-red-700 mt-1">
            [{status.lastError.code}] {status.lastError.message}
          </div>
          <div className="text-xs text-red-500 mt-1">{new Date(status.lastError.at).toLocaleString()}</div>
        </div>
      )}
    </div>
  )
}
